import { Badge } from '@/components/ui/badge';
import { PDKCircleSmall } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { dealStore } from '@/models/stores';
import { observer } from 'mobx-react-lite';

type DealRapidReconAlertBadgeProps = {
  dealId: string;
  className?: string;
  size?: 'default' | 'compact';
};

export const DealRapidReconAlertBadge = observer(
  ({ dealId, className, size = 'default' }: DealRapidReconAlertBadgeProps) => {
    const deal = dealStore.getById(dealId);

    const isInProgress = deal?.status === 'in_progress';
    const isAwaitingDelivery = deal?.status === 'awaiting_delivery';
    const isValidDealStatus = isInProgress || isAwaitingDelivery;

    const isRRReady = deal?.rapid_recon_step === 'Ready For Sale';
    // Show only when the deal is in progress or awaiting delivery
    if (!isValidDealStatus) return null;

    const isCompact = size === 'compact';
    const label = isRRReady ? 'RR ready' : 'RR not ready';

    return (
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <div>
            <Badge
              className={cn(
                'flex flex-row items-center gap-1 text-black bg-white font-normal text-xs shadow-none rounded-full border-foreground/10 pointer-events-none pl-1 pr-2',
                className,
              )}
            >
              <PDKCircleSmall
                className={cn(
                  'size-4',
                  isRRReady
                    ? 'text-[var(--color-emerald-500)] fill-[var(--color-emerald-500)]'
                    : 'text-[var(--color-amber-500)] fill-[var(--color-amber-500)]',
                )}
              />

              <span className={cn(isCompact && 'truncate')}>{label}</span>
            </Badge>
          </div>
        </TooltipTrigger>

        <TooltipContent>
          <div className="flex flex-col gap-1 text-xs">
            {isRRReady ? (
              <div>
                <div>
                  Rapid Recon is <b>Ready For Sale</b>.
                </div>
                <div>You can proceed with contracting for this deal.</div>
              </div>
            ) : (
              <div>
                <div>
                  This deal is in <b>Contracting</b>, but Rapid Recon is{' '}
                  <b>Not Ready For Sale</b>.
                </div>
                <div>
                  The deal cannot complete until RR is marked “Ready For Sale”.
                </div>
                <div>
                  Update the RR step on the deal if the vehicle is ready.
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  },
);
